<template>
  <v-card class="terms-of-use">
    <v-card-title class="headline blue lighten-5">
      KOLOBOX TERMS OF USE
    </v-card-title>
    <v-card-text>
      <p class="effective-date"><strong>Effective Date:</strong> 20th July, 2026</p>

      <p>
        These Terms of Use ("Terms") govern your access to and use of the
        Kolobox mobile application, website, and related services (collectively,
        the "Services") provided by Kolobox Capital Limited ("Kolobox", "we",
        "our", or "us").
      </p>
      <p>
        By creating an account or using the Services, you acknowledge that you
        have read, understood, and agree to be bound by these Terms and our
        Privacy Policy.
      </p>

      <section>
        <h2>1. Eligibility</h2>
        <p>To use the Services, you must:</p>
        <ul>
          <li>Be at least 18 years old or the age of legal majority under Nigerian law.</li>
          <li>Have the legal capacity to enter into a binding agreement.</li>
          <li>Provide accurate, complete, and current information.</li>
          <li>Comply with all applicable laws and regulations.</li>
        </ul>
        <p>Kolobox reserves the right to refuse, suspend, or terminate access where eligibility requirements are not met.</p>
      </section>

      <section>
        <h2>2. Account Registration</h2>
        <p>To access certain Services, you must register for a Kolobox account.</p>
        <p>You agree to:</p>
        <ul>
          <li>Provide truthful and complete information.</li>
          <li>Keep your profile information up to date.</li>
          <li>Maintain the confidentiality of your password, PIN, OTPs, and other security credentials.</li>
          <li>Notify Kolobox immediately if you suspect unauthorized access to your account.</li>
        </ul>
        <p>You are responsible for all activities carried out using your account unless caused by Kolobox's negligence or system failure.</p>
      </section>

      <section>
        <h2>3. Identity Verification (KYC)</h2>
        <p>To comply with applicable laws and regulatory requirements, Kolobox may require identity verification.</p>
        <p>You may be required to provide:</p>
        <ul>
          <li>Government-issued identification.</li>
          <li>Bank Verification Number (BVN), National Identification Number (NIN), or other approved identification.</li>
          <li>Selfie verification.</li>
          <li>Proof of address.</li>
          <li>Any additional information reasonably required under applicable anti-money laundering and counter-terrorism financing laws.</li>
        </ul>
        <p>Failure to complete verification may result in restrictions on your account.</p>
      </section>

      <section>
        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Services for unlawful purposes.</li>
          <li>Engage in fraud, money laundering, terrorist financing, or other financial crimes.</li>
          <li>Upload malicious software or attempt to compromise the security of the Services.</li>
          <li>Reverse engineer, copy, modify, or interfere with the Services.</li>
          <li>Use another person's identity or account.</li>
          <li>Circumvent security measures.</li>
        </ul>
        <p>Kolobox may suspend or permanently terminate accounts involved in prohibited activities.</p>
      </section>

      <section>
        <h2>5. Electronic Communications</h2>
        <p>By using the Services, you consent to receive electronic communications from Kolobox, including:</p>
        <ul>
          <li>Transaction alerts.</li>
          <li>Security notifications.</li>
          <li>Account updates.</li>
          <li>Regulatory notices.</li>
          <li>Changes to these Terms.</li>
          <li>Marketing communications (where permitted by law and subject to your preferences).</li>
        </ul>
        <p>Electronic communications satisfy any legal requirement for written communication.</p>
      </section>

      <section>
        <h2>6. Services</h2>
        <p>Kolobox provides digital financial services, which may include:</p>
        <ul>
          <li>Digital savings.</li>
          <li>Investment opportunities (where applicable).</li>
          <li>Wallet services.</li>
          <li>Payment processing.</li>
          <li>Financial education.</li>
          <li>Other financial products introduced from time to time.</li>
        </ul>
        <p>Availability of specific products may depend on regulatory approvals and your account status.</p>
      </section>

      <section>
        <h2>7. Fees and Charges</h2>
        <p>Applicable fees will be disclosed before transactions are completed.</p>
        <p>You authorize Kolobox to deduct applicable charges from your account where permitted.</p>
        <p>Government taxes, levies, and regulatory charges may also apply.</p>
        <p>Kolobox reserves the right to revise fees after giving reasonable notice where required.</p>
      </section>

      <section>
        <h2>8. User Responsibilities</h2>
        <p>You are responsible for:</p>
        <ul>
          <li>Protecting your login credentials.</li>
          <li>Keeping your registered phone number and email address current.</li>
          <li>Reviewing transaction notifications.</li>
          <li>Reporting unauthorized transactions promptly.</li>
          <li>Using the Services responsibly.</li>
        </ul>
        <p>Failure to protect your account credentials may affect your eligibility for reimbursement where permitted by law.</p>
      </section>

      <section>
        <h2>9. Privacy</h2>
        <p>Your personal information is processed in accordance with the Kolobox Privacy Policy and applicable Nigerian data protection laws, including the Nigeria Data Protection Act, 2023.</p>
        <p>By using the Services, you consent to the collection, storage, processing, and disclosure of your information as described in our Privacy Policy.</p>
      </section>

      <section>
        <h2>10. Intellectual Property</h2>
        <p>All intellectual property rights in the Services, including software, trademarks, logos, graphics, content, designs, and documentation, belong to Kolobox or its licensors.</p>
        <p>Nothing in these Terms transfers ownership of any intellectual property to you.</p>
        <p>You may not copy, distribute, modify, reproduce, or commercially exploit any part of the Services without prior written permission.</p>
      </section>

      <section>
        <h2>11. Suspension and Termination</h2>
        <p>Kolobox may suspend, restrict, or terminate your account where:</p>
        <ul>
          <li>Required by law.</li>
          <li>Required by a regulatory authority.</li>
          <li>Fraud or suspicious activity is detected.</li>
          <li>You breach these Terms.</li>
          <li>You provide false information.</li>
          <li>Continued access presents security or financial risks.</li>
        </ul>
        <p>You may close your account at any time, subject to settlement of outstanding obligations and applicable regulatory requirements.</p>
      </section>

      <section>
        <h2>12. Indemnity</h2>
        <p>You agree to indemnify, defend, and hold harmless Kolobox, its affiliates, directors, officers, employees, agents, licensors, and service providers from all claims, losses, damages, liabilities, costs, and expenses arising from:</p>
        <ul>
          <li>Your use of the Services.</li>
          <li>Your breach of these Terms.</li>
          <li>Your violation of applicable laws.</li>
          <li>Your infringement of third-party rights.</li>
          <li>Fraudulent or unlawful conduct.</li>
        </ul>
      </section>

      <section>
        <h2>13. Assignment</h2>
        <p>You may not assign or transfer your rights under these Terms without Kolobox's written consent.</p>
        <p>Kolobox may assign these Terms to any successor or affiliated entity in connection with a merger, acquisition, restructuring, or sale of its business.</p>
      </section>

      <section>
        <h2>14. Governing Law</h2>
        <p>These Terms shall be governed by the laws of the Federal Republic of Nigeria.</p>
      </section>

      <section>
        <h2>15. Dispute Resolution</h2>
        <p>The parties shall first attempt to resolve disputes through good-faith negotiations.</p>
        <p>Where resolution is unsuccessful within thirty (30) days, the dispute shall be referred to arbitration under the Arbitration and Mediation Act, 2023.</p>
        <p>The arbitration shall:</p>
        <ul>
          <li>Take place in Lagos State, Nigeria.</li>
          <li>Be conducted in English.</li>
          <li>Be heard by a single arbitrator.</li>
          <li>Produce a final and binding decision.</li>
        </ul>
        <p>Nothing prevents either party from seeking urgent interim relief from a court of competent jurisdiction.</p>
      </section>

      <section>
        <h2>16. Limitation of Liability</h2>
        <p>Kolobox shall not be liable for indirect, incidental, consequential, special, or punitive damages arising from use of the Services.</p>
        <p>Kolobox shall not be liable for losses caused by:</p>
        <ul>
          <li>Force majeure events.</li>
          <li>Internet interruptions.</li>
          <li>Third-party payment providers.</li>
          <li>User negligence.</li>
          <li>Unauthorized disclosure of login credentials by the user.</li>
        </ul>
        <p>Where liability cannot legally be excluded, Kolobox's aggregate liability shall not exceed the greater of:</p>
        <ul>
          <li>₦20,000; or</li>
          <li>The total fees paid by the user to Kolobox during the preceding twelve (12) months.</li>
        </ul>
        <p>Nothing excludes liability that cannot be excluded under Nigerian law.</p>
      </section>

      <section>
        <h2>17. Miscellaneous</h2>
        <p>These Terms, together with the Privacy Policy and any other policies published by Kolobox, constitute the entire agreement between you and Kolobox.</p>
        <p>If any provision is held invalid, the remaining provisions shall continue in effect.</p>
        <p>Failure by Kolobox to enforce any provision does not constitute a waiver of future enforcement.</p>
      </section>

      <section>
        <h2>18. Changes to the Terms</h2>
        <p>Kolobox may amend these Terms to reflect legal, regulatory, operational, or business changes.</p>
        <p>Where changes materially affect users' rights, reasonable notice will be provided through the App, website, email, SMS, or other electronic means before the revised Terms take effect.</p>
        <p>Continued use of the Services after the effective date constitutes acceptance of the updated Terms.</p>
      </section>

      <section>
        <h2>19. Contact Us</h2>
        <p>For questions, complaints, or support regarding these Terms or the Services, please contact:</p>
        <address>
          <strong>Kolobox Capital Limited</strong><br />
          Email: <a href="mailto:contact@kolobox.ng">contact@kolobox.ng</a><br />
          Phone: <a href="tel:+2348160031344">+2348160031344</a><br />
          Address: PC 12, Churchgate Street, Victoria Island, Lagos, Nigeria.
        </address>
      </section>
    </v-card-text>
    <v-card-actions><v-spacer /></v-card-actions>
  </v-card>
</template>

<script>
export default {};
</script>

<style scoped>
.terms-of-use .v-card__text > p:first-child {
  margin-top: 0;
}

.effective-date {
  color: #244784;
}

section {
  margin-top: 28px;
}

h2 {
  margin-bottom: 12px;
  color: #244784;
  font-size: 20px;
  line-height: 1.4;
}

p,
ul {
  margin-bottom: 12px;
}

ul {
  padding-left: 24px;
}

li + li {
  margin-top: 6px;
}

address {
  font-style: normal;
}

a {
  color: #35569a;
}
</style>
